// Arithmetic and Geometric Sequences Knowledge Graph Samples
// Organized by difficulty levels with systematic naming

export const arithmeticGeometricSequencesSamples = {
  fundamental: [
    {
      id: 'sequence_fund_01',
      problem: "Find the 10th term of the arithmetic sequence: 2, 5, 8, 11, ...",
      problemType: "arithmetic_geometric_sequences",
      difficulty: "fundamental",
      description: "Basic arithmetic sequence with common difference",
      graphData: {
        nodes: [
          {
            id: "first_term",
            name: "First Term",
            type: "concepts",
            description: "The first term of the sequence",
            value: "a₁ = 2",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "common_difference",
            name: "Common Difference",
            type: "concepts",
            description: "The difference between consecutive terms",
            value: "d = 3",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "nth_term_formula",
            name: "Nth Term Formula",
            type: "formulas",
            description: "aₙ = a₁ + (n-1)d",
            value: "aₙ = 2 + (n-1)3",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "calculation",
            name: "Calculation",
            type: "values",
            description: "Substitute n = 10",
            value: "a₁₀ = 2 + (10-1)3 = 2 + 27 = 29",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The 10th term",
            value: "29",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "identify_first_term",
            name: "Identify First Term",
            type: "steps",
            description: "Extract the first term from the sequence",
            value: "Step 1: a₁ = 2",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "find_common_difference",
            name: "Find Common Difference",
            type: "steps",
            description: "Calculate the difference between consecutive terms",
            value: "Step 2: d = 5-2 = 3",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "apply_formula",
            name: "Apply Formula",
            type: "steps",
            description: "Use the nth term formula with n=10",
            value: "Step 3: a₁₀ = 2 + (10-1)3",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "calculate_result",
            name: "Calculate Result",
            type: "steps",
            description: "Perform the calculation",
            value: "Step 4: a₁₀ = 2 + 27 = 29",
            importance: 10,
            x: 0, y: 150, z: 20
          }
        ],
        links: [
          { from: "first_term", to: "nth_term_formula", type: "used in", strength: 9 },
          { from: "common_difference", to: "nth_term_formula", type: "used in", strength: 9 },
          { from: "nth_term_formula", to: "calculation", type: "gives", strength: 10 },
          { from: "calculation", to: "result", type: "equals", strength: 10 },
          { from: "identify_first_term", to: "find_common_difference", type: "enables", strength: 8 },
          { from: "find_common_difference", to: "apply_formula", type: "enables", strength: 9 },
          { from: "apply_formula", to: "calculate_result", type: "leads to", strength: 10 },
          { from: "calculate_result", to: "result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'sequence_fund_02',
      problem: "Find the 8th term of the geometric sequence: 3, 6, 12, 24, ...",
      problemType: "arithmetic_geometric_sequences",
      difficulty: "fundamental",
      description: "Basic geometric sequence with common ratio",
      graphData: {
        nodes: [
          {
            id: "first_term",
            name: "First Term",
            type: "concepts",
            description: "The first term of the sequence",
            value: "a₁ = 3",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "common_ratio",
            name: "Common Ratio",
            type: "concepts",
            description: "The ratio between consecutive terms",
            value: "r = 2",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "nth_term_formula",
            name: "Nth Term Formula",
            type: "formulas",
            description: "aₙ = a₁ × r^(n-1)",
            value: "aₙ = 3 × 2^(n-1)",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "calculation",
            name: "Calculation",
            type: "values",
            description: "Substitute n = 8",
            value: "a₈ = 3 × 2^7 = 3 × 128 = 384",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The 8th term",
            value: "384",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "identify_first_term",
            name: "Identify First Term",
            type: "steps",
            description: "Extract the first term from the sequence",
            value: "Step 1: a₁ = 3",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "find_common_ratio",
            name: "Find Common Ratio",
            type: "steps",
            description: "Calculate the ratio between consecutive terms",
            value: "Step 2: r = 6/3 = 2",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "apply_formula",
            name: "Apply Formula",
            type: "steps",
            description: "Use the nth term formula with n=8",
            value: "Step 3: a₈ = 3 × 2^(8-1)",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "calculate_result",
            name: "Calculate Result",
            type: "steps",
            description: "Perform the calculation",
            value: "Step 4: a₈ = 3 × 128 = 384",
            importance: 10,
            x: 0, y: 150, z: 20
          }
        ],
        links: [
          { from: "first_term", to: "nth_term_formula", type: "used in", strength: 9 },
          { from: "common_ratio", to: "nth_term_formula", type: "used in", strength: 9 },
          { from: "nth_term_formula", to: "calculation", type: "gives", strength: 10 },
          { from: "calculation", to: "result", type: "equals", strength: 10 },
          { from: "identify_first_term", to: "find_common_ratio", type: "enables", strength: 8 },
          { from: "find_common_ratio", to: "apply_formula", type: "enables", strength: 9 },
          { from: "apply_formula", to: "calculate_result", type: "leads to", strength: 10 },
          { from: "calculate_result", to: "result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'sequence_fund_03',
      problem: "Find the common difference of the arithmetic sequence: 7, 11, 15, 19, ...",
      problemType: "arithmetic_geometric_sequences",
      difficulty: "fundamental",
      description: "Finding common difference from sequence",
      graphData: {
        nodes: [
          {
            id: "sequence",
            name: "Sequence",
            type: "concepts",
            description: "The given arithmetic sequence",
            value: "7, 11, 15, 19, ...",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "difference_calculation",
            name: "Difference Calculation",
            type: "values",
            description: "Calculate difference between consecutive terms",
            value: "11 - 7 = 4, 15 - 11 = 4, 19 - 15 = 4",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "common_difference",
            name: "Common Difference",
            type: "values",
            description: "The constant difference",
            value: "d = 4",
            importance: 10,
            x: 150, y: -100, z: 20
          }
        ],
        links: [
          { from: "sequence", to: "difference_calculation", type: "analyzed by", strength: 9 },
          { from: "difference_calculation", to: "common_difference", type: "gives", strength: 10 }
        ]
      }
    },
    {
      id: 'sequence_fund_04',
      problem: "Find the common ratio of the geometric sequence: 2, 6, 18, 54, ...",
      problemType: "arithmetic_geometric_sequences",
      difficulty: "fundamental",
      description: "Finding common ratio from sequence",
      graphData: {
        nodes: [
          {
            id: "sequence",
            name: "Sequence",
            type: "concepts",
            description: "The given geometric sequence",
            value: "2, 6, 18, 54, ...",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "ratio_calculation",
            name: "Ratio Calculation",
            type: "values",
            description: "Calculate ratio between consecutive terms",
            value: "6/2 = 3, 18/6 = 3, 54/18 = 3",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "common_ratio",
            name: "Common Ratio",
            type: "values",
            description: "The constant ratio",
            value: "r = 3",
            importance: 10,
            x: 150, y: -100, z: 20
          }
        ],
        links: [
          { from: "sequence", to: "ratio_calculation", type: "analyzed by", strength: 9 },
          { from: "ratio_calculation", to: "common_ratio", type: "gives", strength: 10 }
        ]
      }
    },
    {
      id: 'sequence_fund_05',
      problem: "Find the sum of the first 5 terms of the arithmetic sequence: 1, 4, 7, 10, 13",
      problemType: "arithmetic_geometric_sequences",
      difficulty: "fundamental",
      description: "Basic arithmetic series sum",
      graphData: {
        nodes: [
          {
            id: "sequence",
            name: "Sequence",
            type: "concepts",
            description: "The arithmetic sequence",
            value: "1, 4, 7, 10, 13",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "sum_formula",
            name: "Sum Formula",
            type: "formulas",
            description: "Sₙ = n(a₁ + aₙ)/2",
            value: "S₅ = 5(1 + 13)/2",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "calculation",
            name: "Calculation",
            type: "values",
            description: "Calculate the sum",
            value: "S₅ = 5(14)/2 = 35",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The sum of first 5 terms",
            value: "35",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "sequence", to: "sum_formula", type: "used in", strength: 9 },
          { from: "sum_formula", to: "calculation", type: "gives", strength: 10 },
          { from: "calculation", to: "result", type: "equals", strength: 10 }
        ]
      }
    }
  ],
  
  intermediate: [
    {
      id: 'sequence_inter_01',
      problem: "Find the sum of the first 10 terms of the arithmetic sequence with a₁ = 3 and d = 4",
      problemType: "arithmetic_geometric_sequences",
      difficulty: "intermediate",
      description: "Arithmetic series with given parameters",
      graphData: {
        nodes: [
          {
            id: "first_term",
            name: "First Term",
            type: "concepts",
            description: "Given first term",
            value: "a₁ = 3",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "common_difference",
            name: "Common Difference",
            type: "concepts",
            description: "Given common difference",
            value: "d = 4",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "nth_term_formula",
            name: "Nth Term Formula",
            type: "formulas",
            description: "aₙ = a₁ + (n-1)d",
            value: "a₁₀ = 3 + (10-1)4 = 39",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "sum_formula",
            name: "Sum Formula",
            type: "formulas",
            description: "Sₙ = n(a₁ + aₙ)/2",
            value: "S₁₀ = 10(3 + 39)/2",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "calculation",
            name: "Calculation",
            type: "values",
            description: "Calculate the sum",
            value: "S₁₀ = 10(42)/2 = 210",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The sum of first 10 terms",
            value: "210",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "first_term", to: "nth_term_formula", type: "used in", strength: 9 },
          { from: "common_difference", to: "nth_term_formula", type: "used in", strength: 9 },
          { from: "nth_term_formula", to: "sum_formula", type: "used in", strength: 9 },
          { from: "sum_formula", to: "calculation", type: "gives", strength: 10 },
          { from: "calculation", to: "result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'sequence_inter_02',
      problem: "Find the sum of the first 6 terms of the geometric sequence with a₁ = 2 and r = 3",
      problemType: "arithmetic_geometric_sequences",
      difficulty: "intermediate",
      description: "Geometric series with given parameters",
      graphData: {
        nodes: [
          {
            id: "first_term",
            name: "First Term",
            type: "concepts",
            description: "Given first term",
            value: "a₁ = 2",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "common_ratio",
            name: "Common Ratio",
            type: "concepts",
            description: "Given common ratio",
            value: "r = 3",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "sum_formula",
            name: "Sum Formula",
            type: "formulas",
            description: "Sₙ = a₁(1-rⁿ)/(1-r)",
            value: "S₆ = 2(1-3⁶)/(1-3)",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "calculation",
            name: "Calculation",
            type: "values",
            description: "Calculate the sum",
            value: "S₆ = 2(1-729)/(-2) = 2(-728)/(-2) = 728",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The sum of first 6 terms",
            value: "728",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "first_term", to: "sum_formula", type: "used in", strength: 9 },
          { from: "common_ratio", to: "sum_formula", type: "used in", strength: 9 },
          { from: "sum_formula", to: "calculation", type: "gives", strength: 10 },
          { from: "calculation", to: "result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'sequence_inter_03',
      problem: "If the 3rd term of an arithmetic sequence is 8 and the 7th term is 20, find the first term and common difference",
      problemType: "arithmetic_geometric_sequences",
      difficulty: "intermediate",
      description: "Finding sequence parameters from terms",
      graphData: {
        nodes: [
          {
            id: "third_term",
            name: "Third Term",
            type: "concepts",
            description: "Given third term",
            value: "a₃ = 8",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "seventh_term",
            name: "Seventh Term",
            type: "concepts",
            description: "Given seventh term",
            value: "a₇ = 20",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "equation1",
            name: "Equation 1",
            type: "formulas",
            description: "a₃ = a₁ + 2d = 8",
            value: "a₁ + 2d = 8",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "equation2",
            name: "Equation 2",
            type: "formulas",
            description: "a₇ = a₁ + 6d = 20",
            value: "a₁ + 6d = 20",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "subtraction",
            name: "Subtraction",
            type: "values",
            description: "Subtract equations",
            value: "4d = 12, d = 3",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "first_term",
            name: "First Term",
            type: "values",
            description: "Substitute to find a₁",
            value: "a₁ + 6 = 8, a₁ = 2",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "third_term", to: "equation1", type: "gives", strength: 9 },
          { from: "seventh_term", to: "equation2", type: "gives", strength: 9 },
          { from: "equation1", to: "subtraction", type: "used in", strength: 9 },
          { from: "equation2", to: "subtraction", type: "used in", strength: 9 },
          { from: "subtraction", to: "first_term", type: "enables", strength: 10 }
        ]
      }
    },
    {
      id: 'sequence_inter_04',
      problem: "Find the infinite sum of the geometric sequence: 1, 1/2, 1/4, 1/8, ...",
      problemType: "arithmetic_geometric_sequences",
      difficulty: "intermediate",
      description: "Infinite geometric series",
      graphData: {
        nodes: [
          {
            id: "sequence",
            name: "Sequence",
            type: "concepts",
            description: "The geometric sequence",
            value: "1, 1/2, 1/4, 1/8, ...",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "first_term",
            name: "First Term",
            type: "concepts",
            description: "The first term of the sequence",
            value: "a₁ = 1",
            importance: 9,
            x: -200, y: -150, z: 10
          },
          {
            id: "common_ratio",
            name: "Common Ratio",
            type: "values",
            description: "Calculate common ratio",
            value: "r = 1/2",
            importance: 9,
            x: 200, y: -150, z: 10
          },
          {
            id: "convergence_check",
            name: "Convergence Check",
            type: "concepts",
            description: "Check if |r| < 1 for convergence",
            value: "|1/2| = 1/2 < 1 ✓",
            importance: 8,
            x: 0, y: -100, z: 15
          },
          {
            id: "infinite_sum_formula",
            name: "Infinite Sum Formula",
            type: "formulas",
            description: "S = a₁/(1-r) for |r| < 1",
            value: "S = 1/(1-1/2)",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "calculation",
            name: "Calculation",
            type: "values",
            description: "Calculate the sum",
            value: "S = 1/(1/2) = 2",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The infinite sum",
            value: "2",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "step1_identify_sequence",
            name: "Step 1: Identify Sequence",
            type: "steps",
            description: "Recognize the geometric sequence pattern",
            value: "Step 1: Identify geometric sequence 1, 1/2, 1/4, 1/8, ...",
            importance: 8,
            x: -300, y: -50, z: 0
          },
          {
            id: "step2_find_first_term",
            name: "Step 2: Find First Term",
            type: "steps",
            description: "Extract the first term from the sequence",
            value: "Step 2: a₁ = 1",
            importance: 8,
            x: -300, y: 0, z: 0
          },
          {
            id: "step3_calculate_ratio",
            name: "Step 3: Calculate Common Ratio",
            type: "steps",
            description: "Find the ratio between consecutive terms",
            value: "Step 3: r = (1/2)/1 = 1/2",
            importance: 9,
            x: -300, y: 50, z: 0
          },
          {
            id: "step4_check_convergence",
            name: "Step 4: Check Convergence",
            type: "steps",
            description: "Verify that |r| < 1 for infinite sum to exist",
            value: "Step 4: |1/2| = 1/2 < 1 ✓",
            importance: 9,
            x: -300, y: 100, z: 0
          },
          {
            id: "step5_apply_formula",
            name: "Step 5: Apply Infinite Sum Formula",
            type: "steps",
            description: "Use the formula S = a₁/(1-r)",
            value: "Step 5: S = 1/(1-1/2)",
            importance: 10,
            x: 300, y: 0, z: 0
          },
          {
            id: "step6_calculate_result",
            name: "Step 6: Calculate Result",
            type: "steps",
            description: "Perform the final calculation",
            value: "Step 6: S = 1/(1/2) = 2",
            importance: 10,
            x: 300, y: 50, z: 0
          },
          {
            id: "step7_verify_answer",
            name: "Step 7: Verify Answer",
            type: "steps",
            description: "Check that the result makes sense",
            value: "Step 7: The infinite sum converges to 2",
            importance: 8,
            x: 300, y: 100, z: 0
          }
        ],
        links: [
          { from: "sequence", to: "first_term", type: "has", strength: 9 },
          { from: "sequence", to: "common_ratio", type: "analyzed by", strength: 9 },
          { from: "first_term", to: "infinite_sum_formula", type: "used in", strength: 9 },
          { from: "common_ratio", to: "convergence_check", type: "checked in", strength: 9 },
          { from: "convergence_check", to: "infinite_sum_formula", type: "enables", strength: 9 },
          { from: "infinite_sum_formula", to: "calculation", type: "gives", strength: 10 },
          { from: "calculation", to: "result", type: "equals", strength: 10 },
          { from: "step1_identify_sequence", to: "step2_find_first_term", type: "enables", strength: 8 },
          { from: "step2_find_first_term", to: "step3_calculate_ratio", type: "enables", strength: 9 },
          { from: "step3_calculate_ratio", to: "step4_check_convergence", type: "enables", strength: 9 },
          { from: "step4_check_convergence", to: "step5_apply_formula", type: "enables", strength: 10 },
          { from: "step5_apply_formula", to: "step6_calculate_result", type: "leads to", strength: 10 },
          { from: "step6_calculate_result", to: "step7_verify_answer", type: "enables", strength: 8 },
          { from: "step7_verify_answer", to: "result", type: "confirms", strength: 8 }
        ]
      }
    },
    {
      id: 'sequence_inter_05',
      problem: "Find the number of terms in the arithmetic sequence: 5, 8, 11, ..., 50",
      problemType: "arithmetic_geometric_sequences",
      difficulty: "intermediate",
      description: "Finding number of terms in sequence",
      graphData: {
        nodes: [
          {
            id: "sequence",
            name: "Sequence",
            type: "concepts",
            description: "The arithmetic sequence",
            value: "5, 8, 11, ..., 50",
            importance: 10,
            x: 0, y: -250, z: 0
          },
          {
            id: "first_term",
            name: "First Term",
            type: "concepts",
            description: "First term of sequence",
            value: "a₁ = 5",
            importance: 10,
            x: -200, y: -150, z: 10
          },
          {
            id: "last_term",
            name: "Last Term",
            type: "concepts",
            description: "Last term of sequence",
            value: "aₙ = 50",
            importance: 9,
            x: 200, y: -150, z: 10
          },
          {
            id: "common_difference",
            name: "Common Difference",
            type: "values",
            description: "Calculate common difference",
            value: "d = 3",
            importance: 9,
            x: 0, y: -100, z: 20
          },
          {
            id: "nth_term_formula",
            name: "Nth Term Formula",
            type: "formulas",
            description: "aₙ = a₁ + (n-1)d",
            value: "50 = 5 + (n-1)3",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "calculation",
            name: "Calculation",
            type: "values",
            description: "Solve for n",
            value: "45 = (n-1)3, n-1 = 15, n = 16",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "Number of terms",
            value: "16",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "step1_identify_sequence",
            name: "Step 1: Identify Sequence",
            type: "steps",
            description: "Recognize the arithmetic sequence pattern",
            value: "Step 1: Identify arithmetic sequence 5, 8, 11, ..., 50",
            importance: 8,
            x: -300, y: -50, z: 0
          },
          {
            id: "step2_find_first_term",
            name: "Step 2: Find First Term",
            type: "steps",
            description: "Extract the first term from the sequence",
            value: "Step 2: a₁ = 5",
            importance: 8,
            x: -300, y: 0, z: 0
          },
          {
            id: "step3_find_last_term",
            name: "Step 3: Find Last Term",
            type: "steps",
            description: "Identify the last term of the sequence",
            value: "Step 3: aₙ = 50",
            importance: 9,
            x: -300, y: 50, z: 0
          },
          {
            id: "step4_calculate_difference",
            name: "Step 4: Calculate Common Difference",
            type: "steps",
            description: "Find the difference between consecutive terms",
            value: "Step 4: d = 8-5 = 3",
            importance: 9,
            x: -300, y: 100, z: 0
          },
          {
            id: "step5_setup_equation",
            name: "Step 5: Setup Equation",
            type: "steps",
            description: "Use the nth term formula to set up equation",
            value: "Step 5: 50 = 5 + (n-1)3",
            importance: 10,
            x: 300, y: -50, z: 0
          },
          {
            id: "step6_solve_equation",
            name: "Step 6: Solve Equation",
            type: "steps",
            description: "Solve the equation for n",
            value: "Step 6: 45 = (n-1)3, n-1 = 15, n = 16",
            importance: 10,
            x: 300, y: 0, z: 0
          },
          {
            id: "step7_verify_answer",
            name: "Step 7: Verify Answer",
            type: "steps",
            description: "Check that the answer makes sense",
            value: "Step 7: The sequence has 16 terms",
            importance: 8,
            x: 300, y: 50, z: 0
          }
        ],
        links: [
          { from: "sequence", to: "first_term", type: "has", strength: 9 },
          { from: "sequence", to: "last_term", type: "has", strength: 9 },
          { from: "first_term", to: "common_difference", type: "used to find", strength: 9 },
          { from: "last_term", to: "nth_term_formula", type: "used in", strength: 9 },
          { from: "common_difference", to: "nth_term_formula", type: "used in", strength: 9 },
          { from: "nth_term_formula", to: "calculation", type: "gives", strength: 10 },
          { from: "calculation", to: "result", type: "equals", strength: 10 },
          { from: "step1_identify_sequence", to: "step2_find_first_term", type: "enables", strength: 8 },
          { from: "step2_find_first_term", to: "step3_find_last_term", type: "enables", strength: 9 },
          { from: "step3_find_last_term", to: "step4_calculate_difference", type: "enables", strength: 9 },
          { from: "step4_calculate_difference", to: "step5_setup_equation", type: "enables", strength: 10 },
          { from: "step5_setup_equation", to: "step6_solve_equation", type: "leads to", strength: 10 },
          { from: "step6_solve_equation", to: "step7_verify_answer", type: "enables", strength: 8 },
          { from: "step7_verify_answer", to: "result", type: "confirms", strength: 8 }
        ]
      }
    }
  ],
  
  harder: [
    {
      id: 'sequence_hard_01',
      problem: "The sum of the first n terms of an arithmetic sequence is Sₙ = 3n² + 2n. Find the first term and common difference",
      problemType: "arithmetic_geometric_sequences",
      difficulty: "harder",
      description: "Finding sequence parameters from sum formula",
      graphData: {
        nodes: [
          {
            id: "given_sum_formula",
            name: "Given Sum Formula",
            type: "concepts",
            description: "Given sum formula",
            value: "Sₙ = 3n² + 2n",
            importance: 10,
            x: 0, y: -250, z: 0
          },
          {
            id: "standard_sum_formula",
            name: "Standard Sum Formula",
            type: "formulas",
            description: "Sₙ = n(a₁ + aₙ)/2",
            value: "Sₙ = n(a₁ + a₁ + (n-1)d)/2",
            importance: 9,
            x: -200, y: -150, z: 10
          },
          {
            id: "simplified_formula",
            name: "Simplified Formula",
            type: "formulas",
            description: "Sₙ = n(2a₁ + (n-1)d)/2",
            value: "Sₙ = n²d/2 + n(a₁ - d/2)",
            importance: 9,
            x: 200, y: -150, z: 10
          },
          {
            id: "coefficient_comparison",
            name: "Coefficient Comparison",
            type: "values",
            description: "Compare coefficients",
            value: "d/2 = 3, a₁ - d/2 = 2",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "common_difference",
            name: "Common Difference",
            type: "values",
            description: "Solve for d",
            value: "d = 6",
            importance: 9,
            x: -150, y: 100, z: -15
          },
          {
            id: "first_term",
            name: "First Term",
            type: "values",
            description: "Solve for a₁",
            value: "a₁ = 5",
            importance: 10,
            x: 150, y: 100, z: -15
          },
          {
            id: "step1_analyze_given_formula",
            name: "Step 1: Analyze Given Formula",
            type: "steps",
            description: "Understand the given sum formula structure",
            value: "Step 1: Given Sₙ = 3n² + 2n",
            importance: 8,
            x: -300, y: -50, z: 0
          },
          {
            id: "step2_recall_standard_formula",
            name: "Step 2: Recall Standard Formula",
            type: "steps",
            description: "Recall the standard arithmetic sum formula",
            value: "Step 2: Sₙ = n(a₁ + aₙ)/2 = n(a₁ + a₁ + (n-1)d)/2",
            importance: 9,
            x: -300, y: 0, z: 0
          },
          {
            id: "step3_simplify_standard_formula",
            name: "Step 3: Simplify Standard Formula",
            type: "steps",
            description: "Simplify the standard formula to match given form",
            value: "Step 3: Sₙ = n²d/2 + n(a₁ - d/2)",
            importance: 9,
            x: -300, y: 50, z: 0
          },
          {
            id: "step4_compare_coefficients",
            name: "Step 4: Compare Coefficients",
            type: "steps",
            description: "Compare coefficients of n² and n terms",
            value: "Step 4: d/2 = 3, a₁ - d/2 = 2",
            importance: 10,
            x: -300, y: 100, z: 0
          },
          {
            id: "step5_solve_for_d",
            name: "Step 5: Solve for Common Difference",
            type: "steps",
            description: "Solve the first equation for d",
            value: "Step 5: d/2 = 3, so d = 6",
            importance: 10,
            x: 300, y: -50, z: 0
          },
          {
            id: "step6_solve_for_a1",
            name: "Step 6: Solve for First Term",
            type: "steps",
            description: "Substitute d into second equation to find a₁",
            value: "Step 6: a₁ - 3 = 2, so a₁ = 5",
            importance: 10,
            x: 300, y: 0, z: 0
          },
          {
            id: "step7_verify_solution",
            name: "Step 7: Verify Solution",
            type: "steps",
            description: "Verify the solution by checking both equations",
            value: "Step 7: a₁ = 5, d = 6 ✓",
            importance: 8,
            x: 300, y: 50, z: 0
          }
        ],
        links: [
          { from: "given_sum_formula", to: "standard_sum_formula", type: "compared to", strength: 9 },
          { from: "standard_sum_formula", to: "simplified_formula", type: "simplified to", strength: 9 },
          { from: "simplified_formula", to: "coefficient_comparison", type: "gives", strength: 10 },
          { from: "coefficient_comparison", to: "common_difference", type: "solves", strength: 10 },
          { from: "coefficient_comparison", to: "first_term", type: "solves", strength: 10 },
          { from: "step1_analyze_given_formula", to: "step2_recall_standard_formula", type: "enables", strength: 8 },
          { from: "step2_recall_standard_formula", to: "step3_simplify_standard_formula", type: "enables", strength: 9 },
          { from: "step3_simplify_standard_formula", to: "step4_compare_coefficients", type: "enables", strength: 10 },
          { from: "step4_compare_coefficients", to: "step5_solve_for_d", type: "enables", strength: 10 },
          { from: "step5_solve_for_d", to: "step6_solve_for_a1", type: "enables", strength: 10 },
          { from: "step6_solve_for_a1", to: "step7_verify_solution", type: "enables", strength: 8 },
          { from: "step7_verify_solution", to: "first_term", type: "confirms", strength: 8 },
          { from: "step7_verify_solution", to: "common_difference", type: "confirms", strength: 8 }
        ]
      }
    },
    {
      id: 'sequence_hard_02',
      problem: "Find the sum of the first 20 terms of the sequence: 1, 3, 6, 10, 15, ... (triangular numbers)",
      problemType: "arithmetic_geometric_sequences",
      difficulty: "harder",
      description: "Triangular number sequence",
      graphData: {
        nodes: [
          {
            id: "sequence_pattern",
            name: "Sequence Pattern",
            type: "concepts",
            description: "Triangular number pattern",
            value: "1, 3, 6, 10, 15, ...",
            importance: 10,
            x: 0, y: -250, z: 0
          },
          {
            id: "triangular_numbers",
            name: "Triangular Numbers",
            type: "concepts",
            description: "Numbers that can form triangular patterns",
            value: "1, 3, 6, 10, 15, ...",
            importance: 9,
            x: -200, y: -150, z: 10
          },
          {
            id: "nth_term_formula",
            name: "Nth Term Formula",
            type: "formulas",
            description: "aₙ = n(n+1)/2",
            value: "aₙ = n(n+1)/2",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "sum_formula",
            name: "Sum Formula",
            type: "formulas",
            description: "Sₙ = Σ(n(n+1)/2) = n(n+1)(n+2)/6",
            value: "S₂₀ = 20(21)(22)/6",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "calculation",
            name: "Calculation",
            type: "values",
            description: "Calculate the sum",
            value: "S₂₀ = 9240/6 = 1540",
            importance: 9,
            x: 0, y: 200, z: 0
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The sum of first 20 terms",
            value: "1540",
            importance: 10,
            x: 0, y: 300, z: 0
          },
          {
            id: "step1_identify_pattern",
            name: "Step 1: Identify Pattern",
            type: "steps",
            description: "Recognize the triangular number pattern",
            value: "Step 1: Identify triangular numbers 1, 3, 6, 10, 15, ...",
            importance: 8,
            x: -300, y: -50, z: 0
          },
          {
            id: "step2_understand_triangular_numbers",
            name: "Step 2: Understand Triangular Numbers",
            type: "steps",
            description: "Understand what triangular numbers represent",
            value: "Step 2: Triangular numbers represent dots in triangular patterns",
            importance: 8,
            x: -300, y: 0, z: 0
          },
          {
            id: "step3_find_nth_term_formula",
            name: "Step 3: Find Nth Term Formula",
            type: "steps",
            description: "Derive or recall the formula for nth triangular number",
            value: "Step 3: aₙ = n(n+1)/2",
            importance: 9,
            x: -300, y: 50, z: 0
          },
          {
            id: "step4_derive_sum_formula",
            name: "Step 4: Derive Sum Formula",
            type: "steps",
            description: "Find the sum formula for triangular numbers",
            value: "Step 4: Sₙ = Σ(n(n+1)/2) = n(n+1)(n+2)/6",
            importance: 10,
            x: -300, y: 100, z: 0
          },
          {
            id: "step5_substitute_n",
            name: "Step 5: Substitute n = 20",
            type: "steps",
            description: "Substitute n = 20 into the sum formula",
            value: "Step 5: S₂₀ = 20(21)(22)/6",
            importance: 9,
            x: 300, y: -50, z: 0
          },
          {
            id: "step6_calculate_result",
            name: "Step 6: Calculate Result",
            type: "steps",
            description: "Perform the calculation",
            value: "Step 6: S₂₀ = 9240/6 = 1540",
            importance: 10,
            x: 300, y: 0, z: 0
          },
          {
            id: "step7_verify_answer",
            name: "Step 7: Verify Answer",
            type: "steps",
            description: "Check that the answer makes sense",
            value: "Step 7: The sum of first 20 triangular numbers is 1540",
            importance: 8,
            x: 300, y: 50, z: 0
          }
        ],
        links: [
          { from: "sequence_pattern", to: "triangular_numbers", type: "represents", strength: 9 },
          { from: "triangular_numbers", to: "nth_term_formula", type: "described by", strength: 9 },
          { from: "nth_term_formula", to: "sum_formula", type: "summed to give", strength: 10 },
          { from: "sum_formula", to: "calculation", type: "gives", strength: 10 },
          { from: "calculation", to: "result", type: "equals", strength: 10 },
          { from: "step1_identify_pattern", to: "step2_understand_triangular_numbers", type: "enables", strength: 8 },
          { from: "step2_understand_triangular_numbers", to: "step3_find_nth_term_formula", type: "enables", strength: 9 },
          { from: "step3_find_nth_term_formula", to: "step4_derive_sum_formula", type: "enables", strength: 10 },
          { from: "step4_derive_sum_formula", to: "step5_substitute_n", type: "enables", strength: 9 },
          { from: "step5_substitute_n", to: "step6_calculate_result", type: "leads to", strength: 10 },
          { from: "step6_calculate_result", to: "step7_verify_answer", type: "enables", strength: 8 },
          { from: "step7_verify_answer", to: "result", type: "confirms", strength: 8 }
        ]
      }
    },
    {
      id: 'sequence_hard_03',
      problem: "A geometric sequence has a₃ = 8 and a₇ = 128. Find the sum of the first 10 terms",
      problemType: "arithmetic_geometric_sequences",
      difficulty: "harder",
      description: "Geometric sequence with given terms",
      graphData: {
        nodes: [
          {
            id: "third_term",
            name: "Third Term",
            type: "concepts",
            description: "Given third term",
            value: "a₃ = 8",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "seventh_term",
            name: "Seventh Term",
            type: "concepts",
            description: "Given seventh term",
            value: "a₇ = 128",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "ratio_equation",
            name: "Ratio Equation",
            type: "formulas",
            description: "a₇/a₃ = r⁴",
            value: "128/8 = r⁴ = 16",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "common_ratio",
            name: "Common Ratio",
            type: "values",
            description: "Solve for r",
            value: "r = 2",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "first_term",
            name: "First Term",
            type: "values",
            description: "Find a₁ using a₃",
            value: "8 = a₁(2)², a₁ = 2",
            importance: 10,
            x: 0, y: 100, z: -15
          },
          {
            id: "sum_calculation",
            name: "Sum Calculation",
            type: "values",
            description: "Calculate sum of first 10 terms",
            value: "S₁₀ = 2(1-2¹⁰)/(1-2) = 2(1-1024)/(-1) = 2046",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "third_term", to: "ratio_equation", type: "used in", strength: 9 },
          { from: "seventh_term", to: "ratio_equation", type: "used in", strength: 9 },
          { from: "ratio_equation", to: "common_ratio", type: "gives", strength: 10 },
          { from: "common_ratio", to: "first_term", type: "used to find", strength: 9 },
          { from: "first_term", to: "sum_calculation", type: "used in", strength: 10 }
        ]
      }
    },
    {
      id: 'sequence_hard_04',
      problem: "Find the sum of the infinite series: 1 + 1/3 + 1/9 + 1/27 + ...",
      problemType: "arithmetic_geometric_sequences",
      difficulty: "harder",
      description: "Infinite geometric series with fraction ratio",
      graphData: {
        nodes: [
          {
            id: "series",
            name: "Series",
            type: "concepts",
            description: "The infinite geometric series",
            value: "1 + 1/3 + 1/9 + 1/27 + ...",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "first_term",
            name: "First Term",
            type: "values",
            description: "First term of series",
            value: "a₁ = 1",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "common_ratio",
            name: "Common Ratio",
            type: "values",
            description: "Ratio between terms",
            value: "r = 1/3",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "infinite_sum_formula",
            name: "Infinite Sum Formula",
            type: "formulas",
            description: "S = a₁/(1-r) for |r| < 1",
            value: "S = 1/(1-1/3)",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "calculation",
            name: "Calculation",
            type: "values",
            description: "Calculate the sum",
            value: "S = 1/(2/3) = 3/2",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The infinite sum",
            value: "3/2",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "series", to: "first_term", type: "has", strength: 9 },
          { from: "series", to: "common_ratio", type: "has", strength: 9 },
          { from: "first_term", to: "infinite_sum_formula", type: "used in", strength: 9 },
          { from: "common_ratio", to: "infinite_sum_formula", type: "used in", strength: 9 },
          { from: "infinite_sum_formula", to: "calculation", type: "gives", strength: 10 },
          { from: "calculation", to: "result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'sequence_hard_05',
      problem: "The sum of the first n terms of a geometric sequence is Sₙ = 2(3ⁿ - 1). Find the first term and common ratio",
      problemType: "arithmetic_geometric_sequences",
      difficulty: "harder",
      description: "Finding geometric sequence parameters from sum formula",
      graphData: {
        nodes: [
          {
            id: "sum_formula",
            name: "Sum Formula",
            type: "concepts",
            description: "Given sum formula",
            value: "Sₙ = 2(3ⁿ - 1)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "standard_sum_formula",
            name: "Standard Sum Formula",
            type: "formulas",
            description: "Sₙ = a₁(1-rⁿ)/(1-r)",
            value: "Sₙ = a₁(1-rⁿ)/(1-r)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "comparison",
            name: "Comparison",
            type: "values",
            description: "Compare formulas",
            value: "a₁/(1-r) = 2, r = 3",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "first_term",
            name: "First Term",
            type: "values",
            description: "Solve for a₁",
            value: "a₁ = 2(1-3) = -4",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "common_ratio",
            name: "Common Ratio",
            type: "values",
            description: "The common ratio",
            value: "r = 3",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "sum_formula", to: "standard_sum_formula", type: "compared to", strength: 9 },
          { from: "standard_sum_formula", to: "comparison", type: "gives", strength: 10 },
          { from: "comparison", to: "first_term", type: "solves", strength: 10 },
          { from: "comparison", to: "common_ratio", type: "gives", strength: 10 }
        ]
      }
    }
  ]
}; 